// Global
import React, { useState, useEffect, useRef } from 'react';
import {
  Field,
  GetStaticComponentProps,
  ImageField,
  Item,
  LinkField,
  SitecoreContextValue,
} from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
// Lib
import clientFactory from 'lib/graphql-client-factory';
import { ComponentProps } from 'lib/component-props';
import { BoKNavigation } from 'lib/templates/Feature.BOKF.model';
import { useRealPathName } from 'lib/utils/useRealPathname';
import { SitecoreIds } from 'lib/constants/sitecore-ids';
import { useCookieRedirection } from 'lib/hooks/use-cookie-redirection';

// Local
import SVG from 'helpers/SVG/SVG';
import Button from 'helpers/Button/Button';
import HeaderQuery from './Header.graphql';
import LinkWrapper from 'helpers/LinkWrapper/LinkWrapper';
import ImageWrapper from 'helpers/ImageWrapper/ImageWrapper';
import { useSearchOverlayContext } from 'helpers/Providers/SearchOverlayContextProvider';
import { usePathname } from 'next/navigation';

type SubItem = {
  Value: { value: string };
};

type ExtendedSitecoreContextProps = SitecoreContextValue & {
  FDICText?: Field<string>;
  FDICLogo?: { url: string; altText: string };
  IncludeFDICLogo?: Field<boolean>;
  DiscludeFDIC?: Field<boolean>;
};

export type ItemType = {
  name?: string;
  displayName?: string;
  template?: { name?: string };
  labelText?: { jsonValue: Field<string> | undefined };
  hideLabelSmallDesktop?: { jsonValue: Field<boolean> | undefined };
  icon?: { jsonValue: Item | undefined | null };
  link?: { jsonValue: LinkField | undefined };
  text?: { jsonValue: Field<string> | undefined };

  children?: {
    results: Array<ItemType>;
  };
};

export type LeprechaunHeaderProps = {
  disableChannelRedirect?: Field<boolean> | undefined;
  enableHomePageLeftNavigation?: Field<boolean> | undefined;
  logo?: { jsonValue: ImageField | undefined };
  logoLink?: { jsonValue: LinkField | undefined };
  link?: { jsonValue: LinkField | undefined };
  rightAlignIcons?: Field<boolean> | undefined;
  children?: {
    results: ItemType[] &
      {
        displayName?: string;
        hideLabelSmallDesktop?: { jsonValue: Field<boolean> };
        icon?: { jsonValue: Item | undefined | null };
        name: string;
        template: { name: string };
        link?: { jsonValue: LinkField | undefined };
        text?: { jsonValue: Field<string> | undefined };
        children: { results: ItemType[] };
        fields?: ItemType | undefined;
      }[];
  };
};

export type SubItemType =
  | {
      id?: string;
      displayName?: string;
      name?: string;
      templateId?: string;
      templateName?: string;
      url?: string;

      fields?: {
        HideLabelSmallDesktop?: { value: boolean };
        icon?: {
          id?: string;
          name?: string;
          displayName?: string;
          url?: string;
          fields: { Value: { value: string } } | null;
        } | null;
        Link?: LinkField | null;
        Text?: { value: string };
        children: {
          id?: string;
          displayName?: string;
          name?: string;
          templateId?: string;
          templateName?: string;
          url?: string;

          fields: {
            HideLabelSmallDesktop?: { value: boolean };
            Icon?: {
              id?: string;
              name?: string;
              displayName?: string;
              url?: string;
              fields: { Value: { value: string } } | null;
            } | null;
            Link?: LinkField | null;
            Text?: { value: string };
            children: {
              name?: string;
              templateId?: string;
              templateName?: string;
              id?: string;
              url?: string;
              HideLabelSmallDesktop?: { value: boolean };
              Icon?: {
                id?: string;
                name?: string;
                displayName?: string;
                url?: string;
                fields: { Value: { value: string } } | null;
              } | null;
              Link?: LinkField | null;
              Text?: { value: string };
              fields: {
                HideLabelSmallDesktop?: { value: boolean };
                Icon?: {
                  id?: string;
                  name?: string;
                  displayName?: string;
                  url?: string;
                  fields: { Value: { value: string } } | null;
                } | null;
                Link?: LinkField | null;
                Text?: { value: string };
                name?: string;
                templateId?: string;
                templateName?: string;
                id?: string;
                url?: string;
              };
            }[];
          };
        }[];
      };
    }
  | undefined;

export type HeaderProps = ComponentProps &
  BoKNavigation.NavigationConfiguration & {
    staticProps?: {
      header?: LeprechaunHeaderProps;
    };
  };

export interface SubMenuItem {
  open: boolean;
  subItem: ItemType;
}

export type ColorItemProps = {
  fields: {
    Value: {
      value?: string;
    };
  };
};

export type CategoryItemProps = {
  fields: {
    Color: ColorItemProps;
  };
};

export interface SitecoreContextType {
  sitecoreContext?: {
    route?: {
      templateId?: string;
      fields?: {
        Category?: CategoryItemProps[];
      };
    };
  };
}

const Header = (serverProps: HeaderProps): JSX.Element => {
  const props = { fields: serverProps?.staticProps?.header };

  const { overlayOpen, setOverlayOpen } = useSearchOverlayContext();

  // check if the current page temmplate is Article or VideoResource and apply category specific classes
  const sitecoreContext = useSitecoreContext();

  const routeData = (sitecoreContext as SitecoreContextType)?.sitecoreContext?.route;
  const isPageTypeArticleOrVideoResource = (() => {
    for (const [, { templateId, templateName }] of Object.entries(SitecoreIds.Route)) {
      if (templateId === routeData?.templateId) {
        return templateName;
      }
    }
    return undefined;
  })();
  let activeCategoryofPage: string | undefined = undefined;
  if (isPageTypeArticleOrVideoResource && routeData?.fields?.Category?.[0]) {
    activeCategoryofPage =
      routeData?.fields?.Category?.[0]?.fields?.Color?.fields?.Value?.value || '';
  }

  const id = serverProps.params?.RenderingIdentifier || '';
  const headerUtilityMobile = serverProps?.staticProps?.header?.children?.results.find(
    (elem) => elem.template?.name === 'HeaderUtilityFolderMobile'
  );

  const utilityNavMobile = serverProps?.staticProps?.header?.children?.results.find(
    (elem) => elem.template?.name === 'UtilityNavigationFolderMobile'
  );

  const extendedSitecoreContext: ExtendedSitecoreContextProps =
    useSitecoreContext().sitecoreContext;

  const FDICImage = extendedSitecoreContext.FDICLogo?.url;
  const FDICImageAltText = extendedSitecoreContext.FDICLogo?.altText;
  const FDICText = extendedSitecoreContext.FDICText?.value;
  const includeFDICLogo = extendedSitecoreContext.IncludeFDICLogo?.value;
  const discludeFDIC = extendedSitecoreContext.route?.fields?.['DiscludeFDIC'] as Field<boolean>;
  const isFDICExists = includeFDICLogo && !discludeFDIC?.value;

  function filterFunc(value: ItemType) {
    return value?.template?.name === 'NavigationLink';
  }

  function filterFunc2(value: ItemType) {
    return value?.template?.name === 'Promo Button';
  }

  function filterFunc3(value: ItemType) {
    return value?.template?.name === 'HeaderUtilityFolderDesktop';
  }

  function filterFunc4(value: ItemType) {
    return value?.template?.name === 'UtilityNavigationFolderDesktop';
  }

  const primaryNavData = serverProps?.staticProps?.header?.children?.results.filter(filterFunc);

  const headerUtilityData = serverProps?.staticProps?.header?.children?.results.filter(filterFunc3);

  const utilityNavData = serverProps?.staticProps?.header?.children?.results.filter(filterFunc4);

  const subscribeButton = serverProps?.staticProps?.header?.children?.results.filter(filterFunc2);

  const isHomepageNavEnabled = serverProps?.fields?.EnableHomePageLeftNavigation?.value;

  //Get all lob channel from header props

  useCookieRedirection(serverProps?.fields?.DisableChannelRedirect?.value, primaryNavData);

  const [isOpen, setOpen] = useState(false);

  const [isSubMenuOpen, setSubMenuOpen] = useState<SubMenuItem>({
    open: false,
    subItem: {},
  });

  const pathname = useRealPathName();
  const strippedPath = pathname?.split('/', 2); // get the first item in path

  const navRef = useRef<HTMLDivElement>(null);

  const updateHeight = () => {
    if (navRef.current) {
      const height = navRef.current.offsetHeight;
      document.documentElement.style.setProperty('--nav-height', `${height}px`);
    }
  };

  useEffect(() => {
    updateHeight();

    window.addEventListener('resize', updateHeight);

    return () => {
      window.removeEventListener('resize', updateHeight);
    };
  }, []);

  function getLocation(href: string) {
    const match = href.match(
      /^(https?\:)\/\/(([^:\/?#]*)(?:\:([0-9]+))?)([\/]{0,1}[^?#]*)(\?[^#]*|)(#.*|)$/
    );
    return (
      match && {
        href: href,
        protocol: match[1],
        host: match[2],
        hostname: match[3],
        port: match[4],
        pathname: match[5],
        search: match[6],
        hash: match[7],
      }
    );
  }

  useEffect(() => {
    document.documentElement.style.overflow = isOpen ? 'hidden' : '';

    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    isOpen &&
      primaryNavData?.length === 1 &&
      setSubMenuOpen({
        open: true,
        subItem: primaryNavData[0],
      });
  }, [isOpen]);

  const handleHamburgerMenuClick = () => {
    if (window.innerWidth > 1024) {
      setOpen(false);
      setSubMenuOpen({ open: false, subItem: {} });
      document.documentElement.style.overflow = '';
    } else {
      setOpen((prev) => !prev);
      setSubMenuOpen({ open: false, subItem: {} });
      document.documentElement.style.overflow = isOpen ? '' : 'hidden';
    }
  };

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 767) {
        setOpen(false);
        setSubMenuOpen({ open: false, subItem: {} });
        document.documentElement.style.overflow = '';
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  const pathName = usePathname();
  useEffect(() => {
    setOpen(false);
    setSubMenuOpen({ open: false, subItem: {} });
  }, [pathName]);

  const toggleSearchOverlay = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();
    setOverlayOpen(!overlayOpen);
  };

  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (isOpen || isSubMenuOpen.open) {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Tab' && containerRef.current) {
          const focusableElements = containerRef.current.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
          ) as NodeListOf<HTMLElement>;
          const firstElement = focusableElements[0];
          const lastElement = focusableElements[focusableElements.length - 1];

          if (event.shiftKey) {
            // Shift + Tab
            if (document.activeElement === firstElement) {
              event.preventDefault();
              lastElement.focus();
            }
          } else {
            // Tab
            if (document.activeElement === lastElement) {
              event.preventDefault();
              firstElement.focus();
            }
          }
        }
      };

      const container = containerRef.current;
      if (container) {
        container.addEventListener('keydown', handleKeyDown);
      }

      return () => {
        if (container) {
          container.removeEventListener('keydown', handleKeyDown);
        }
      };
    }
    // Ensure that the useEffect hook always returns undefined
    return undefined;
  }, [isOpen, isSubMenuOpen.open]);

  const [scrollPosition, setScrollPosition] = useState(0);
  const [prevScrollPosition, setPrevScrollPosition] = useState(0);
  const [isScrollingUp, setIsScrollingUp] = useState(false);
  const [alertNoticeHeight, setAlertNoticeHeight] = useState(0);
  const [hasAlerts, setHasAlerts] = useState(false);

  const handleScroll = () => {
    const currentPosition = window.scrollY;
    setIsScrollingUp(currentPosition < prevScrollPosition);
    setPrevScrollPosition(currentPosition);
    setScrollPosition(currentPosition);
  };

  useEffect(() => {
    if (hasAlerts) {
      updateAlertBannerHeight();
    }
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [prevScrollPosition]);

  const updateAlertBannerHeight = () => {
    // Get all alert containers that aren't hidden
    const alertContainers = document.querySelectorAll('.alert-container:not(.hidden)');
    if (alertContainers.length === 0) {
      setHasAlerts(false);
      document.documentElement.style.removeProperty('--alert-notice-height');
    } else {
      const headerElement = document.querySelector('header');
      let totalHeight = 0;

      // Only process alerts that are above the header
      alertContainers.forEach((container) => {
        if (headerElement) {
          const alertRect = container.getBoundingClientRect();
          const headerRect = headerElement.getBoundingClientRect();

          // Check if alert is above header
          if (alertRect.top < headerRect.top) {
            totalHeight += container.clientHeight;
          }
        }
      });

      // Update the CSS variable with total height
      setHasAlerts(true);
      document.documentElement.style.setProperty('--alert-notice-height', `${totalHeight}px`);
    }
  };

  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    updateAlertBannerHeight();
    window.addEventListener('resize', updateAlertBannerHeight);

    // Cleanup function
    return () => {
      window.removeEventListener('resize', updateAlertBannerHeight);
    };
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const alertHeight = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--alert-notice-height')
      );
      setAlertNoticeHeight(alertHeight);

      // Add listener for alert height changes
      const handleAlertHeightChange = (event: Event) => {
        const customEvent = event as CustomEvent;
        if (customEvent.detail && customEvent.detail.alertHeight !== undefined) {
          setAlertNoticeHeight(customEvent.detail.alertHeight);
          setHasAlerts(customEvent.detail.alertHeight > 0);
        }
      };

      document.addEventListener('alertheightchanged', handleAlertHeightChange);

      // Clean up event listener on unmount
      return () => {
        document.removeEventListener('alertheightchanged', handleAlertHeightChange);
      };
    }
    return undefined; // Explicitly return undefined for non-cleanup paths
  }, []);

  const isScrolledPastAlert = scrollPosition > alertNoticeHeight;
  const progressPastAlert = isScrolledPastAlert ? scrollPosition - alertNoticeHeight : 0;

  // Add this constant to track when progressPastAlert is >= 100
  const isPastAlert100 = progressPastAlert >= 100;

  return (
    <header
      ref={containerRef}
      id={`${id ? id : ''}`}
      className={`${isOpen ? 'fixed w-full' : 'sticky'} top-0 z-20 ${
        isScrolledPastAlert && isPastAlert100 && !isScrollingUp ? 'header-hidden' : 'header-show'
      }`}
      data-id="header-component"
    >
      {/* Main Nav */}
      <div
        ref={navRef}
        className={`relative w-full flex z-50 px-4 sm:px-6 lg:px-8 gap-x-2 sm:gap-x-6 lg:gap-x-0 bg-light-background-neutral-variant h-12 sm:h-14 before:content-[''] before:absolute before:top-12 sm:before:top-14 before:border-l-[45px] before:border-solid before:border-l-transparent before:border-t-[45px] before:border-t-light-stroke-inverse before:h-0 before:w-0 before:right-0 before:z-10 ${
          isOpen || isSubMenuOpen.open ? 'absolute' : ''
        } ${isFDICExists ? '' : 'shadow-small'}`}
        data-id="header-desktop-nav"
      >
        <button type="button" onClick={handleHamburgerMenuClick} className="btn-hamburger-menu">
          <SVG svg="icon-menu"></SVG>
        </button>
        <LinkWrapper
          field={props?.fields?.logoLink?.jsonValue?.value || {}}
          className="max-w-[18rem] w-full my-1 md:my-2 md:mr-2 px-2 md:px-4"
          data-id="header-logo-link"
        >
          {/* BOKF Logo */}
          <ImageWrapper
            className="w-full h-full"
            field={props?.fields?.logo?.jsonValue}
            height="56"
            width="256"
          />
        </LinkWrapper>
        {primaryNavData && primaryNavData.length > 0 && (
          <ul className="hidden lg:flex gap-x-1 lg:gap-x-3 xl:gap-x-8">
            {primaryNavData.length > 1 &&
              primaryNavData?.map((item, key) => {
                const itemHref = item?.link?.jsonValue?.value?.href;
                const location = getLocation(item?.link?.jsonValue?.value?.href || '')
                  ?.pathname.replace('/', '')
                  .toLowerCase();
                const pathComparison = strippedPath[1].toLowerCase();
                const categoryClass = item?.link?.jsonValue?.value?.class || '';
                let activeClass =
                  'font-medium text-light-fill-primary border-light-stroke-secondary-brand bg-light-background-neutral-low hover:text-light-fill-primary focus:border-light-stroke-primary-variant focus:text-light-fill-primary';
                let otherClass =
                  'text-light-fill-primary-variant border-transparent hover:border-light-stroke-secondary-brand hover:text-light-fill-primary dark:hover:border-light-stroke-secondary-brand focus:border-light-stroke-primary-variant focus:text-light-fill-primary';

                type CategoryKey = 'category-1' | 'category-2' | 'category-3' | 'category-4';

                const categoryClasses: Record<
                  CategoryKey,
                  { activeClass: string; otherClass: string }
                > = {
                  'category-1': {
                    activeClass:
                      'font-medium text-light-fill-primary border-light-other-yourBusiness bg-light-background-neutral-low hover:text-light-fill-primary focus:outline focus:outline-light-stroke-primary-variant focus:outline-2 focus:rounded focus:outline-offset-[-2px] focus:text-light-fill-primary',
                    otherClass:
                      'text-light-fill-primary-variant border-transparent hover:border-light-other-yourBusiness hover:text-light-fill-primary dark:hover:border-light-other-yourBusiness focus:border-light-other-yourBusiness focus:outline focus:outline-light-stroke-primary-variant focus:outline-2 focus:rounded focus:outline-offset-[-2px] focus:text-light-fill-primary',
                  },
                  'category-2': {
                    activeClass:
                      'font-medium text-light-fill-primary border-light-other-perspectives bg-light-background-neutral-low hover:text-light-fill-primary focus:outline focus:outline-light-stroke-primary-variant focus:outline-2 focus:rounded focus:outline-offset-[-2px] focus:text-light-fill-primary',
                    otherClass:
                      'text-light-fill-primary-variant border-transparent hover:border-light-other-perspectives hover:text-light-fill-primary dark:hover:border-light-other-perspectives focus:border-light-other-perspectives focus:outline focus:outline-light-stroke-primary-variant focus:outline-2 focus:rounded focus:outline-offset-[-2px] focus:text-light-fill-primary',
                  },
                  'category-3': {
                    activeClass:
                      'font-medium text-light-fill-primary border-light-other-community bg-light-background-neutral-low hover:text-light-fill-primary focus:outline focus:outline-light-stroke-primary-variant focus:outline-2 focus:rounded focus:outline-offset-[-2px] focus:text-light-fill-primary',
                    otherClass:
                      'text-light-fill-primary-variant border-transparent hover:border-light-other-community hover:text-light-fill-primary dark:hover:border-light-other-community focus:border-light-other-community focus:outline focus:outline-light-stroke-primary-variant focus:outline-2 focus:rounded focus:outline-offset-[-2px] focus:text-light-fill-primary',
                  },
                  'category-4': {
                    activeClass:
                      'font-medium text-light-fill-primary border-light-other-yourMoney bg-light-background-neutral-low hover:text-light-fill-primary focus:outline focus:outline-light-stroke-primary-variant focus:outline-2 focus:rounded focus:outline-offset-[-2px] focus:text-light-fill-primary',
                    otherClass:
                      'text-light-fill-primary-variant border-transparent hover:border-light-other-yourMoney hover:text-light-fill-primary dark:hover:border-light-other-yourMoney focus:border-light-other-yourMoney focus:outline focus:outline-light-stroke-primary-variant focus:outline-2 focus:rounded focus:outline-offset-[-2px] focus:text-light-fill-primary',
                  },
                };

                if (categoryClasses[categoryClass as CategoryKey]) {
                  activeClass = categoryClasses[categoryClass as CategoryKey].activeClass;
                  otherClass = categoryClasses[categoryClass as CategoryKey].otherClass;
                }

                const homePageActiveState =
                  key === 0 && pathComparison === '' && isHomepageNavEnabled;

                const linkClassNames = classNames(
                  'p1 h-full content-center pt-1 px-3 lg:px-6 whitespace-nowrap inline-block text-center',
                  'before:block before:content-[attr(title)] before:font-medium before:h-0 before:overflow-hidden before:invisible',
                  'focus:bg-light-background-neutral-low focus:font-medium focus:outline-none focus:no-underline',
                  'hover:bg-light-background-neutral-low hover:font-medium border-b-4 hover:no-underline',
                  {
                    // Active class when the path matches, show category-specific border
                    [activeClass]:
                      homePageActiveState ||
                      (pathComparison.length && pathComparison === location) ||
                      pathComparison === itemHref?.replace('/', '').toLowerCase() ||
                      activeCategoryofPage === categoryClass,
                    // Inactive state with transparent border, show category-specific border on hover
                    [otherClass]:
                      !homePageActiveState &&
                      !(pathComparison.length && pathComparison === location) &&
                      pathComparison !== itemHref?.replace('/', '').toLowerCase() &&
                      activeCategoryofPage !== categoryClass,
                  }
                );

                return (
                  <li key={key}>
                    <LinkWrapper
                      field={item?.link?.jsonValue?.value || {}}
                      title={item?.text?.jsonValue?.value}
                      className={linkClassNames}
                    >
                      {item?.text?.jsonValue?.value}
                    </LinkWrapper>
                  </li>
                );
              })}
          </ul>
        )}
        {/* Location/Languages */}
        {headerUtilityData?.[0]?.children?.results &&
        headerUtilityData?.[0]?.children?.results?.length > 0 ? (
          <ul className="hidden lg:flex flex-row ml-auto items-center divide-x divide-light-stroke-primary-high font-medium xl:flex">
            {headerUtilityData?.[0]?.children?.results.map((item, key) => {
              return (
                <li key={key} className="md:mr-1 lg:mr-4 last:mr-0 md:pl-1 lg:pl-4 first:pl-0">
                  <LinkWrapper
                    field={item.link?.jsonValue?.value || {}}
                    className="p3-medium flex items-center p-2 text-light-fill-primary-variant hover:text-light-fill-secondary hover:no-underline hover:bg-light-background-neutral-low focus:outline-light-stroke-primary-variant focus-visible:outline-light-stroke-primary-variant rounded"
                  >
                    <SVG
                      className="pr-0 xl:pr-2"
                      svg={(item.icon?.jsonValue?.fields as SubItem)?.Value?.value}
                      hidden={false}
                    ></SVG>
                    <span className="hidden xl:block whitespace-nowrap">
                      {item.text?.jsonValue?.value}
                    </span>
                  </LinkWrapper>
                </li>
              );
            })}
          </ul>
        ) : null}
        {/* END Location/Languages */}
        {/* Mobile Login */}

        <LinkWrapper
          field={{
            value: {
              href: headerUtilityMobile?.children?.results[0]?.link?.jsonValue?.value?.href || '/',
            },
          }}
          className={`group flex gap-x-2 lg:hidden ml-auto my-auto p2 text-light-fill-primary-variant hover:text-light-fill-primary-variant group-hover:underline items-center ${headerUtilityMobile?.children?.results[0]?.link?.jsonValue?.value?.class}`}
        >
          <SVG
            className="p-1 border-transparent rounded group-hover:bg-light-background-neutral-low group-hover:text-light-fill-secondary"
            svg={
              (headerUtilityMobile?.children?.results[0]?.icon?.jsonValue?.fields as SubItem)?.Value
                ?.value
            }
          ></SVG>
          <span className="hidden xs:block">
            {headerUtilityMobile?.children?.results[0]?.text?.jsonValue?.value}
          </span>
        </LinkWrapper>
        {/* END Mobile Login */}
      </div>

      <div>
        {/* FDIC */}
        {isFDICExists && (
          <div
            className={`relative z-[42] lg:z-0 w-full h-[45px] md:h-11 bg-light-background-neutral-high pr-9 lg:pr-[450px] pl-2 sm:pl-8 lg:pl-10 py-3 text-[13px] flex items-center gap-x-2 ${
              isFDICExists ? 'shadow-small' : ''
            }`}
          >
            <ImageWrapper
              className="w-full h-full max-w-fit"
              field={{ value: { src: FDICImage, alt: FDICImageAltText } }}
              height="17"
              width="41"
            />
            <p className="italic">{FDICText}</p>
          </div>
        )}
        {/* END FDIC */}

        <nav
          className="hidden lg:flex absolute top-14 right-0 h-auto ml-auto z-10"
          data-id="utility-nav"
        >
          <SVG svg="utility-nav-red-triangle" hidden={false}></SVG>
          <ul className="flex h-11 pr-11 items-center bg-light-background-secondary-brand text-light-fill-inverse divide-x divide-light-stroke-primary">
            {utilityNavData?.[0]?.children?.results.map((item, key) => {
              const isSearchLink = item?.link?.jsonValue?.value?.class === 'jsMenuSearch';
              return (
                <li key={key} className="px-2">
                  <LinkWrapper
                    field={{
                      value: item?.link?.jsonValue?.value?.href
                        ? { href: item?.link?.jsonValue?.value?.href }
                        : {},
                    }}
                    className={`flex gap-2 px-2 py-1 items-center font-normal text-light-fill-inverse hover:text-light-fill-inverse hover:bg-light-background-secondary-brand-high hover:no-underline border-transparent rounded focus:outline-light-fill-inverse focus:bg-light-background-secondary-brand-high focus:outline focus:outline-2 focus:no-underline ${item?.link?.jsonValue?.value.class}`}
                    onClick={isSearchLink ? (e) => toggleSearchOverlay(e) : undefined}
                  >
                    {(item?.icon?.jsonValue?.fields as SubItem)?.Value?.value && (
                      <SVG
                        svg={(item?.icon?.jsonValue?.fields as SubItem)?.Value?.value}
                        hidden={false}
                      ></SVG>
                    )}
                    {item?.text?.jsonValue?.value}
                  </LinkWrapper>
                </li>
              );
            })}
          </ul>
        </nav>
        {/* END Utility Nav */}
      </div>
      {/* Mobile Nav */}
      <nav
        className={`flex flex-col absolute w-full max-w-[20rem] z-40 border-light-stroke-inverse bg-light-background-neutral-variant overflow-auto bok-scrollbar bok-scrollbar--no-border ${
          isFDICExists
            ? 'h-[calc(100dvh-var(--nav-height)-44px)] top-[calc(var(--nav-height)+44px)]'
            : 'h-[calc(100dvh-var(--nav-height))] top-[calc(var(--nav-height))]'
        } ${isOpen && !isSubMenuOpen.open ? 'px-8 py-6 justify-between' : 'justify-normal'} ${
          !isOpen ? 'hidden' : ''
        }`}
      >
        {primaryNavData && primaryNavData.length > 1 && isSubMenuOpen.open && (
          <div className="flex p-6 justify-between border-b-2 p3-medium border-b-light-stroke-secondary-brand bg-light-background-neutral items-center ">
            <button
              tabIndex={0}
              className="btn-all-categories"
              onClick={() => setSubMenuOpen({ open: false, subItem: {} })}
              aria-label="Mobile Navigation Menu Button"
            >
              <SVG svg="icon-chevron-left"></SVG>
              All categories
            </button>
          </div>
        )}
        {/* Mobile Primary Nav */}
        {isOpen && !isSubMenuOpen.open && primaryNavData && primaryNavData.length > 0 && (
          <ul className="flex flex-col gap-y-2">
            {primaryNavData?.map((item, key) => {
              return (
                <li key={key}>
                  <button
                    tabIndex={0}
                    className=" btn-mobile-primary-link"
                    onClick={() => setSubMenuOpen({ open: true, subItem: item })}
                    aria-label="Mobile Navigation Menu Button"
                  >
                    {item?.text?.jsonValue?.value}
                    <SVG className="p-1 [&_svg]:h-8 [&_svg]:w-8" svg="icon-chevron-right"></SVG>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
        {/* END Mobile Primary Nav */}
        <div
          className={`h-full flex flex-col ${
            isOpen && !isSubMenuOpen.open ? 'justify-end' : 'justify-between'
          }`}
        >
          {isSubMenuOpen.open && (
            <>
              <ul className={`${isOpen && !isSubMenuOpen.open ? '' : 'mx-8 mt-8 sm:mt-6'}`}>
                <li className="mb-4 sm:mb-6">
                  <LinkWrapper
                    field={{
                      value: { href: isSubMenuOpen.subItem.link?.jsonValue?.value.href || '/' },
                    }}
                    onClick={() => {
                      setSubMenuOpen({ open: false, subItem: {} });
                      setOpen(false);
                    }}
                    className="flex items-center p-2 gap-x-2 p1-medium focus:outline-light-stroke-primary-variant focus:no-underline hover:no-underline hover:text-light-fill-primary"
                  >
                    {isSubMenuOpen?.subItem?.text?.jsonValue?.value}
                    <SVG className="[&_svg]:w-5 [&_svg]:h-5" svg="icon-arrow-right"></SVG>
                  </LinkWrapper>
                </li>
                {isSubMenuOpen.subItem?.children?.results.map((item: ItemType, key: number) => {
                  // if (Object.hasOwn(item, 'icon')) {
                  return (
                    <React.Fragment key={key}>
                      {/* Static Label */}
                      {item.labelText?.jsonValue?.value && (
                        <li
                          key={key}
                          className={`c1 text-light-fill-primary-variant pl-2 ${
                            key > 0
                              ? 'mt-4 sm:mt-6 pt-4 sm:pt-6 border-t stroke-light-stroke-primary'
                              : ''
                          }`}
                        >
                          {item.labelText?.jsonValue?.value}
                        </li>
                      )}
                      {/* END Static Label */}
                      {/* Secondary Links */}
                      {item.link?.jsonValue?.value && (
                        <li>
                          <LinkWrapper
                            field={item.link?.jsonValue || {}}
                            className="flex p-2 p2-medium rounded hover:text-light-fill-primary hover:no-underline hover:bg-light-background-neutral-high active:bg-light-background-neutral-high active:border-light-stroke-primary-variant focus:outline-light-stroke-primary-variant  focus:no-underline focus:bg-light-background-neutral-high"
                          >
                            {item.text?.jsonValue?.value}
                          </LinkWrapper>
                          {/* Tertiary Links */}
                          {item?.children?.results && item?.children?.results.length > 0 && (
                            <ul>
                              {item?.children?.results?.map((item: ItemType, key: number) => {
                                const textValue = item.text?.jsonValue?.value;
                                const hrefValue = item.link?.jsonValue?.value?.href;

                                if (!textValue || !hrefValue) {
                                  return null; // Skip rendering if textValue is empty
                                }
                                return (
                                  <li className="flex" key={key}>
                                    <LinkWrapper
                                      field={item.link?.jsonValue || {}}
                                      className="w-full py-2 pr-2 pl-4 p2 rounded hover:bg-light-background-neutral-high hover:text-light-fill-primary hover:no-underline focus:no-underline focus:bg-light-background-neutral-high focus:outline-light-stroke-primary-variant active:bg-light-background-neutral-high active:border-light-stroke-primary-variant"
                                    >
                                      {textValue}
                                    </LinkWrapper>
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                          {/* END Tertiary Links */}
                        </li>
                      )}
                      {/* END Secondary Links */}
                    </React.Fragment>
                  );
                })}
              </ul>
            </>
          )}
          {/* Mobile Utility */}
          <ul
            className={`flex flex-col mt-4 sm:mt-6 pt-4 sm:pt-6 border-t stroke-light-stroke-primary ${
              isOpen && !isSubMenuOpen.open ? '' : 'mx-8 pb-8 sm:pb-6'
            }`}
          >
            {utilityNavMobile?.children?.results?.map((item, key) => {
              const isSearchLink = item?.link?.jsonValue?.value?.class === 'jsMenuSearch';
              return (
                <li className="flex" key={key}>
                  <LinkWrapper
                    field={item?.link?.jsonValue?.value || {}}
                    className="flex items-center gap-x-2 p-2 rounded group p3 text-light-fill-primary-variant hover:text-light-fill-primary-variant hover:bg-light-background-neutral-high focus:outline-light-stroke-primary-variant focus:no-underline focus:bg-light-background-neutral-high active:underline"
                    onClick={isSearchLink ? (e) => toggleSearchOverlay(e) : undefined}
                  >
                    {(item?.icon?.jsonValue?.fields as SubItem)?.Value?.value && (
                      <SVG
                        className="[&_path]:stroke-light-fill-primary-variant"
                        svg={(item?.icon?.jsonValue?.fields as SubItem)?.Value?.value}
                        hidden={false}
                      ></SVG>
                    )}
                    {item?.text?.jsonValue?.value}
                  </LinkWrapper>
                </li>
              );
            })}
            {/* Subscribe Buttons */}
            {subscribeButton?.[0]?.children?.results?.map((item, key) => {
              return (
                <li className="mt-4 sm:mt-6" key={key}>
                  <Button
                    tag="a"
                    type="auxiliary"
                    size="full"
                    href={item?.link?.jsonValue?.value.href}
                    label={item?.text?.jsonValue?.value}
                    title={item?.text?.jsonValue?.value}
                    fieldClass={item?.link?.jsonValue?.value?.class}
                  />
                </li>
              );
            })}
            {/* END Subscribe Buttons */}
          </ul>
          {/* END Mobile Utility */}
        </div>
      </nav>
      {/* END Mobile Nav */}

      {/* Overlay for mobile nav */}
      <div
        onClick={handleHamburgerMenuClick}
        className={`before:content-[''] before:block before:h-screen before:left-0 before:opacity-95 before:overflow-y-auto before:fixed before:top-0 before:w-screen before:z-10 before:bg-[#352b2b] ${
          !isOpen ? 'hidden' : ''
        }`}
      ></div>
      {/* END Main Nav */}
    </header>
  );
};

export default Header;

export const getStaticProps: GetStaticComponentProps = async (rendering, layoutData) => {
  const graphQLClient = clientFactory({});
  const result = await graphQLClient.request<unknown>(HeaderQuery, {
    datasource: rendering.dataSource,
    language: layoutData?.sitecore?.context?.language,
  });
  return {
    staticProps: result,
  };
};
